import React, { useMemo } from 'react';
import { SparklesIcon, AlertTriangleIcon, TrendingDownIcon, TrendingUpIcon, LightbulbIcon } from '../icons/FinanceIcons';

// --- Insight generators: each returns an insight object or null ---

const insightGenerators = [
  // Lost revenue from no-shows / late cancellations
  (data) => {
    const { lostRevenue } = data;
    if (!lostRevenue || lostRevenue.total <= 0) return null;
    const noShows = lostRevenue.noShows?.count || 0;
    const lateCancels = lostRevenue.lateCancellations?.count || 0;
    const total = noShows + lateCancels;
    if (total === 0) return null;

    const parts = [];
    if (noShows > 0) parts.push(`${noShows} no-show${noShows !== 1 ? 's' : ''}`);
    if (lateCancels > 0) parts.push(`${lateCancels} late cancellation${lateCancels !== 1 ? 's' : ''}`);

    return {
      id: 'lost-revenue',
      type: 'danger',
      priority: 85 + Math.min(total * 3, 15),
      icon: TrendingDownIcon,
      iconBgColor: '#fee2e2',
      iconColor: '#dc2626',
      metric: `£${lostRevenue.total.toLocaleString()} lost to ${parts.join(' and ')} this period.`,
      advice: `Enable mandatory deposits or a stricter cancellation policy to protect this revenue.`,
      actionLabel: 'Review policy',
      actionDescription: 'Review cancellation & deposit policy',
      confirmTitle: 'Review Cancellation Policy',
      confirmMessage: `You're losing £${lostRevenue.total.toLocaleString()} per period to missed appointments. We recommend enabling deposits for new clients and enforcing a 24-hour cancellation window.`,
      successMessage: 'Policy review noted'
    };
  },

  // Rebooking rate dropping
  (data) => {
    const { rebookingData } = data;
    if (!rebookingData || rebookingData.value === 0) return null;
    if (rebookingData.change >= -2) return null;

    return {
      id: 'rebooking-drop',
      type: 'danger',
      priority: 80 + Math.min(Math.abs(rebookingData.change), 20),
      icon: TrendingDownIcon,
      iconBgColor: '#fee2e2',
      iconColor: '#dc2626',
      metric: `Rebooking rate dropped ${Math.abs(rebookingData.change).toFixed(0)}% to ${rebookingData.value}% this period.`,
      advice: `Enable automated follow-up messages to remind clients to rebook before they forget.`,
      actionLabel: 'Enable reminders',
      actionDescription: 'Enable rebooking reminder messages',
      confirmTitle: 'Enable Rebooking Reminders',
      confirmMessage: `This will activate automated SMS/email reminders for clients who haven't rebooked within 48 hours of their appointment. Industry data shows this can improve rebooking by 15-20%.`,
      successMessage: 'Reminders enabled'
    };
  },

  // Client retention dropping
  (data) => {
    const { performanceMetrics } = data;
    const retention = performanceMetrics?.clientRetention;
    if (!retention || retention.value === 0) return null;
    if (retention.change >= -2) return null;

    return {
      id: 'retention-drop',
      type: 'danger',
      priority: 82 + Math.min(Math.abs(retention.change), 18),
      icon: TrendingDownIcon,
      iconBgColor: '#fee2e2',
      iconColor: '#dc2626',
      metric: `Client retention fell ${Math.abs(retention.change).toFixed(0)}% to ${retention.value}% — fewer clients are returning.`,
      advice: `Consider a loyalty programme or personalised check-in messages to re-engage lapsed clients.`,
      actionLabel: 'Set up loyalty',
      actionDescription: 'Explore loyalty programme options',
      confirmTitle: 'Improve Client Retention',
      confirmMessage: `A ${Math.abs(retention.change).toFixed(0)}% drop in retention means you need more new clients just to stand still. A simple loyalty reward (e.g. every 5th visit gets 20% off) can dramatically improve repeat visits.`,
      successMessage: 'Loyalty setup noted'
    };
  },

  // Staff utilization near capacity
  (data) => {
    const { performanceMetrics } = data;
    const util = performanceMetrics?.staffUtilization;
    if (!util || util.value < 85) return null;

    return {
      id: 'high-utilization',
      type: 'warning',
      priority: 75,
      icon: AlertTriangleIcon,
      iconBgColor: '#fef3c7',
      iconColor: '#d97706',
      metric: `Your team is ${util.value}% utilised — you're nearly at capacity.`,
      advice: `Consider a small price increase on peak-time services to maximise revenue from limited slots.`,
      actionLabel: 'Review pricing',
      actionDescription: 'Review peak-time service pricing',
      confirmTitle: 'Review Pricing Strategy',
      confirmMessage: `With ${util.value}% utilisation, you have limited room to grow through volume. A 5-10% price increase on peak slots could significantly improve margins without losing clients.`,
      successMessage: 'Pricing review noted'
    };
  },

  // Staff utilization too low
  (data) => {
    const { performanceMetrics } = data;
    const util = performanceMetrics?.staffUtilization;
    if (!util || util.value === 0 || util.value >= 50) return null;

    return {
      id: 'low-utilization',
      type: 'warning',
      priority: 70 + (50 - util.value),
      icon: AlertTriangleIcon,
      iconBgColor: '#fef3c7',
      iconColor: '#d97706',
      metric: `Staff utilisation is only ${util.value}% — there are empty slots going unfilled.`,
      advice: `Run a last-minute deals promotion or off-peak discounts to fill the gaps in your diary.`,
      actionLabel: 'Create promotion',
      actionDescription: 'Set up a promotional offer',
      confirmTitle: 'Fill Empty Slots',
      confirmMessage: `With ${util.value}% utilisation, you have significant capacity to fill. A targeted promotion for off-peak hours could help drive bookings without cannibalising full-price demand.`,
      successMessage: 'Promotion setup noted'
    };
  },

  // Average spend declining
  (data) => {
    const { metricsData } = data;
    const avgSpend = metricsData?.avgSpend;
    if (!avgSpend || avgSpend.value === 0) return null;
    if (avgSpend.change >= -3) return null;

    return {
      id: 'avg-spend-drop',
      type: 'warning',
      priority: 65 + Math.min(Math.abs(avgSpend.change), 15),
      icon: AlertTriangleIcon,
      iconBgColor: '#fef3c7',
      iconColor: '#d97706',
      metric: `Average spend per client dropped ${Math.abs(avgSpend.change).toFixed(0)}% to £${avgSpend.value.toFixed(0)} this period.`,
      advice: `Upsell add-on treatments and premium products to bring the average ticket back up.`,
      actionLabel: 'View services',
      actionDescription: 'Review upsell opportunities',
      confirmTitle: 'Boost Average Spend',
      confirmMessage: `Your average client spend has dropped to £${avgSpend.value.toFixed(0)}. Consider adding treatment upgrades, conditioning add-ons, or premium product bundles to your service menu.`,
      successMessage: 'Services reviewed'
    };
  },

  // New clients dropping
  (data) => {
    const { metricsData } = data;
    const newClients = metricsData?.newClients;
    if (!newClients) return null;
    if (newClients.value === 0 && newClients.previous === 0) return null;
    if (newClients.change >= -5) return null;

    return {
      id: 'new-clients-drop',
      type: 'warning',
      priority: 60 + Math.min(Math.abs(newClients.change) / 2, 20),
      icon: AlertTriangleIcon,
      iconBgColor: '#fef3c7',
      iconColor: '#d97706',
      metric: `New client acquisition dropped ${Math.abs(newClients.change).toFixed(0)}% compared to last period.`,
      advice: `Ask your happiest clients for Google reviews and consider running a referral offer to attract new faces.`,
      actionLabel: 'Boost marketing',
      actionDescription: 'Explore marketing options',
      confirmTitle: 'Attract More New Clients',
      confirmMessage: `New client numbers are down ${Math.abs(newClients.change).toFixed(0)}%. The most effective low-cost strategies: ask for Google reviews after every great appointment, offer existing clients a referral incentive, and keep your online booking page and socials updated.`,
      successMessage: 'Marketing review noted'
    };
  },

  // Low retail attach rate
  (data) => {
    const { performanceMetrics } = data;
    const retail = performanceMetrics?.retailAttachRate;
    if (!retail || retail.value === 0) return null;
    if (retail.value >= 20) return null;

    return {
      id: 'low-retail',
      type: 'info',
      priority: 55 + (20 - retail.value),
      icon: LightbulbIcon,
      iconBgColor: '#e0e7ff',
      iconColor: '#4f46e5',
      metric: `Only ${retail.value}% of appointments include a retail product sale.`,
      advice: `Train your team to recommend take-home products at every appointment — even small improvements add up fast.`,
      actionLabel: 'View tips',
      actionDescription: 'View retail selling tips',
      confirmTitle: 'Boost Retail Sales',
      confirmMessage: `The industry average retail attach rate is 20-30%. Improving from ${retail.value}% could unlock significant additional monthly revenue. Start by ensuring every stylist recommends at least one product per client.`,
      successMessage: 'Tips viewed'
    };
  },

  // Low rebooking rate (not dropping, just low)
  (data) => {
    const { rebookingData } = data;
    if (!rebookingData || rebookingData.value === 0) return null;
    // Don't duplicate with the "drop" insight
    if (rebookingData.change < -2) return null;
    if (rebookingData.value >= 50) return null;

    return {
      id: 'low-rebooking',
      type: 'info',
      priority: 50,
      icon: LightbulbIcon,
      iconBgColor: '#e0e7ff',
      iconColor: '#4f46e5',
      metric: `Your rebooking rate is ${rebookingData.value}% — the industry target is 60%+.`,
      advice: `Have your team book the next appointment before clients leave the chair to build the rebooking habit.`,
      actionLabel: 'Learn more',
      actionDescription: 'Learn rebooking best practices',
      confirmTitle: 'Improve Rebooking Rate',
      confirmMessage: `Top salons achieve 60-80% rebooking rates. The simplest change: always offer to book the next appointment before the client leaves. Pair this with automated reminders for those who don't rebook on the day.`,
      successMessage: 'Tips noted'
    };
  },

  // Outstanding commission to collect
  (data) => {
    const { commissionData } = data;
    if (!commissionData || commissionData.length === 0) return null;
    const totalOutstanding = commissionData.reduce((sum, s) => sum + (s.outstanding || 0), 0);
    const staffWithOutstanding = commissionData.filter(s => s.outstanding > 0).length;
    if (totalOutstanding <= 0) return null;

    return {
      id: 'commission-outstanding',
      type: 'info',
      priority: 35 + Math.min(totalOutstanding / 50, 30),
      icon: LightbulbIcon,
      iconBgColor: '#e0e7ff',
      iconColor: '#4f46e5',
      metric: `£${totalOutstanding.toFixed(0)} in commission is outstanding across ${staffWithOutstanding} staff member${staffWithOutstanding !== 1 ? 's' : ''}.`,
      advice: `Collect outstanding commissions promptly to keep your cash flow healthy.`,
      actionLabel: 'View commission',
      actionDescription: 'View commission tracker',
      confirmTitle: 'Outstanding Commission',
      confirmMessage: `You have £${totalOutstanding.toFixed(2)} in unpaid commission across ${staffWithOutstanding} team member${staffWithOutstanding !== 1 ? 's' : ''}. Scroll down to the Commission Tracker to collect individual payments.`,
      successMessage: 'Commission reviewed'
    };
  },

  // --- Positive / opportunity insights (lower priority, fill the card when things are healthy) ---

  // Revenue growing strongly
  (data) => {
    const { revenueSummary } = data;
    if (!revenueSummary || revenueSummary.change === null || revenueSummary.change === undefined) return null;
    if (revenueSummary.change <= 5) return null;

    return {
      id: 'revenue-growth',
      type: 'success',
      priority: 30 + Math.min(revenueSummary.change, 20),
      icon: TrendingUpIcon,
      iconBgColor: '#d1fae5',
      iconColor: '#059669',
      metric: `Revenue is up ${revenueSummary.change.toFixed(0)}% compared to the previous period — strong momentum.`,
      advice: `Double down on what's working — consider expanding your team or adding new high-demand services.`,
      actionLabel: 'View breakdown',
      actionDescription: 'View revenue breakdown',
      confirmTitle: 'Revenue Growth',
      confirmMessage: `Great progress! Your revenue grew ${revenueSummary.change.toFixed(0)}% this period. To maintain momentum, identify which services and staff are driving the growth and invest more there.`,
      successMessage: 'Breakdown reviewed'
    };
  },

  // New clients growing
  (data) => {
    const { metricsData } = data;
    const newClients = metricsData?.newClients;
    if (!newClients || newClients.value === 0) return null;
    if (newClients.change <= 5) return null;

    return {
      id: 'new-clients-up',
      type: 'success',
      priority: 28 + Math.min(newClients.change / 2, 15),
      icon: TrendingUpIcon,
      iconBgColor: '#d1fae5',
      iconColor: '#059669',
      metric: `New client acquisition is up ${newClients.change.toFixed(0)}% — ${newClients.value} new clients this period.`,
      advice: `Make sure every new client rebooks before leaving to convert them into regulars.`,
      actionLabel: 'View clients',
      actionDescription: 'View new clients list',
      confirmTitle: 'New Client Growth',
      confirmMessage: `You're attracting ${newClients.change.toFixed(0)}% more new clients. The next step is retention — ensure your team offers a rebooking at checkout and sends a follow-up message within 48 hours.`,
      successMessage: 'Clients reviewed'
    };
  },

  // Strong rebooking rate
  (data) => {
    const { rebookingData } = data;
    if (!rebookingData || rebookingData.value < 60) return null;

    return {
      id: 'strong-rebooking',
      type: 'success',
      priority: 22,
      icon: TrendingUpIcon,
      iconBgColor: '#d1fae5',
      iconColor: '#059669',
      metric: `Rebooking rate is ${rebookingData.value}% — above the industry average of 50%.`,
      advice: `You're building a loyal client base. Consider introducing a VIP tier or referral bonus to reward your best clients.`,
      actionLabel: 'Explore loyalty',
      actionDescription: 'Explore loyalty programme options',
      confirmTitle: 'Strong Rebooking',
      confirmMessage: `A ${rebookingData.value}% rebooking rate puts you ahead of most salons. To capitalise, consider a VIP programme for clients who rebook consistently — exclusive discounts or priority booking can deepen loyalty further.`,
      successMessage: 'Loyalty explored'
    };
  },

  // Average spend growing
  (data) => {
    const { metricsData } = data;
    const avgSpend = metricsData?.avgSpend;
    if (!avgSpend || avgSpend.value === 0) return null;
    if (avgSpend.change <= 5) return null;

    return {
      id: 'avg-spend-up',
      type: 'success',
      priority: 25,
      icon: TrendingUpIcon,
      iconBgColor: '#d1fae5',
      iconColor: '#059669',
      metric: `Average spend per client is up ${avgSpend.change.toFixed(0)}% to £${avgSpend.value.toFixed(0)}.`,
      advice: `Your upselling is working. Share what your top earners are doing with the rest of the team.`,
      actionLabel: 'View performance',
      actionDescription: 'View staff performance',
      confirmTitle: 'Average Spend Growing',
      confirmMessage: `Your average ticket has increased ${avgSpend.change.toFixed(0)}% to £${avgSpend.value.toFixed(0)}. Identify which add-ons or products are driving this and ensure every team member is trained on recommending them.`,
      successMessage: 'Performance reviewed'
    };
  },

  // Tips are healthy — team morale signal
  (data) => {
    const { tipsData } = data;
    if (!tipsData) return null;
    const totalTips = Array.isArray(tipsData) ? tipsData.reduce((sum, s) => sum + (s.totalTips || 0), 0) : 0;
    if (totalTips <= 0) return null;
    const staffWithTips = tipsData.filter(s => (s.totalTips || 0) > 0).length;
    const totalStaff = tipsData.length;
    if (totalStaff === 0) return null;
    const tipRate = Math.round((staffWithTips / totalStaff) * 100);
    if (tipRate >= 70) return null; // Only show if there's room to improve

    return {
      id: 'tip-spread',
      type: 'info',
      priority: 20,
      icon: LightbulbIcon,
      iconBgColor: '#e0e7ff',
      iconColor: '#4f46e5',
      metric: `Only ${staffWithTips} of ${totalStaff} staff received tips this period.`,
      advice: `Enable tipping at checkout for all team members — clients often tip when prompted but won't ask.`,
      actionLabel: 'Review tipping',
      actionDescription: 'Review tipping setup',
      confirmTitle: 'Improve Tip Distribution',
      confirmMessage: `${staffWithTips} out of ${totalStaff} staff received tips. Enabling a tip prompt at checkout (e.g. "Would you like to add a tip?") typically increases tip frequency by 30-40%.`,
      successMessage: 'Tipping reviewed'
    };
  },

  // Appointments growing
  (data) => {
    const { appointmentsData } = data;
    if (!appointmentsData || appointmentsData.value === 0) return null;
    if (appointmentsData.change <= 5) return null;

    return {
      id: 'appointments-up',
      type: 'success',
      priority: 24,
      icon: TrendingUpIcon,
      iconBgColor: '#d1fae5',
      iconColor: '#059669',
      metric: `Appointments are up ${appointmentsData.change.toFixed(0)}% — ${appointmentsData.value} completed this period.`,
      advice: `Growing demand is great — keep an eye on staff utilisation to make sure you have capacity to sustain it.`,
      actionLabel: 'Check capacity',
      actionDescription: 'Review staff utilisation',
      confirmTitle: 'Appointment Growth',
      confirmMessage: `You completed ${appointmentsData.value} appointments, up ${appointmentsData.change.toFixed(0)}%. If utilisation is above 85%, consider onboarding another team member or extending hours to avoid turning clients away.`,
      successMessage: 'Capacity reviewed'
    };
  }
];

// Calculate the revenue projection from real data
const calculateProjection = (revenueSummary, revenueData) => {
  if (!revenueSummary || revenueSummary.current === 0) return null;

  // If we have projected data points from the API, use actual + projected
  if (revenueData && revenueData.length > 0) {
    const currentTotal = revenueData.reduce((sum, d) => sum + (d.current || 0), 0);
    // Only count projected values for data points where current is null (future dates)
    const projectedTotal = revenueData
      .filter(d => d.current === null && d.projected)
      .reduce((sum, d) => sum + (d.projected || 0), 0);

    if (projectedTotal > 0) {
      return {
        amount: Math.round(currentTotal + projectedTotal),
        percentChange: revenueSummary.change
      };
    }
  }

  // Fallback: just show current period revenue and change
  return {
    amount: Math.round(revenueSummary.current),
    percentChange: revenueSummary.change
  };
};

// AI Insights Card Component
const AIInsightsCard = ({
  revenueSummary,
  revenueData,
  performanceMetrics,
  rebookingData,
  lostRevenue,
  metricsData,
  appointmentsData,
  commissionData,
  tipsData
}) => {

  // Generate insights from real data, memoised so it only recalculates when data changes
  const data = { revenueSummary, revenueData, performanceMetrics, rebookingData, lostRevenue, metricsData, appointmentsData, commissionData, tipsData };

  const insights = useMemo(() => {
    const allInsights = insightGenerators
      .map(gen => gen(data))
      .filter(Boolean);

    // Sort by priority (highest first) and take top 3
    return allInsights.sort((a, b) => b.priority - a.priority).slice(0, 3);
  }, [revenueSummary, performanceMetrics, rebookingData, lostRevenue, metricsData, appointmentsData, commissionData, tipsData]);

  const projection = useMemo(
    () => calculateProjection(revenueSummary, revenueData),
    [revenueSummary, revenueData]
  );


  // Don't render the card if there's no data at all
  const hasAnyData = revenueSummary?.current > 0 || (insights && insights.length > 0);
  if (!hasAnyData) return null;

  return (
    <div style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e5e5e5',
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
        <div style={{ color: '#007EA0' }}>
          <SparklesIcon />
        </div>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0, fontFamily: 'Inter, system-ui, sans-serif' }}>
          AI Insights
        </h3>
      </div>

      {/* Projection Headline */}
      {projection && (
        <p style={{
          fontSize: '15px',
          color: '#6b7280',
          lineHeight: '1.6',
          margin: '0 0 1.5rem 0',
          fontFamily: 'Inter, system-ui, sans-serif'
        }}>
          Based on current booking velocity, you are on track to hit{' '}
          <span style={{ fontWeight: '700', color: '#111827' }}>
            £{projection.amount.toLocaleString()}
          </span>
          {projection.percentChange !== null && projection.percentChange !== undefined && (
            <>
              {' '}
              <span style={{
                fontWeight: '600',
                color: projection.percentChange >= 0 ? '#059669' : '#dc2626',
                backgroundColor: projection.percentChange >= 0 ? '#d1fae5' : '#fee2e2',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '13px'
              }}>
                {projection.percentChange >= 0 ? '+' : ''}{typeof projection.percentChange === 'number' ? projection.percentChange.toFixed(1) : projection.percentChange}%
              </span>
            </>
          )}
        </p>
      )}

      {/* Dynamic Insights */}
      {insights.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {insights.map((scenario) => (
              <div
                key={scenario.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.875rem',
                  padding: '1rem',
                  backgroundColor: '#fafafa',
                  borderRadius: '8px',
                  border: '1px solid #f3f4f6',
                }}
              >
                {/* Icon */}
                <div style={{
                  flexShrink: 0,
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  backgroundColor: scenario.iconBgColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: scenario.iconColor,
                }}>
                  <scenario.icon />
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    fontSize: '14px',
                    color: '#111827',
                    lineHeight: '1.5',
                    margin: '0 0 0.375rem 0',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontWeight: '500',
                  }}>
                    {scenario.metric}
                  </p>
                  <p style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    lineHeight: '1.5',
                    margin: 0,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    <span style={{ color: '#374151', fontWeight: '600' }}>{scenario.advice.split(' ').slice(0, 2).join(' ')}</span>
                    {' '}{scenario.advice.split(' ').slice(2).join(' ')}
                  </p>
                </div>

              </div>
          ))}
        </div>
      ) : (
        <p style={{
          fontSize: '14px',
          color: '#9ca3af',
          textAlign: 'center',
          padding: '2rem 0',
          fontFamily: 'Inter, system-ui, sans-serif'
        }}>
          Not enough data to generate insights yet. Keep booking to unlock AI recommendations.
        </p>
      )}
    </div>
  );
};

export default AIInsightsCard;
